import type { CreateSupplyOfferingInput, SupplyListInput, SupplyRepository, TenantContext } from '@zetyar/data-contracts'
import type { AgreementInfo, SupplyOffering, SupplyReservation, UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

interface SalePeriodMeta {
  readonly salePeriodId: string
  readonly salePeriodName: string
  readonly periodMaxPerUser: number
}

interface OfferingPeriodSource {
  readonly title: string
  readonly description?: string | null
  readonly startDate: string
  readonly endDate: string
  readonly maxPerUser: number
}

const activeReservationStatuses = new Set(['pending', 'paid', 'confirmed', 'delivered'])

function slug(value: string): string {
  return value.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function periodId(name: string, startDate: string, endDate: string, limit: number): string {
  return `${slug(name) || 'periodo'}-${startDate}-${endDate}-${limit}`
}

function parseSalePeriodMeta(source: OfferingPeriodSource): SalePeriodMeta {
  if (source.description) {
    try {
      const parsed = JSON.parse(source.description) as Partial<SalePeriodMeta>
      if (parsed.salePeriodId && parsed.salePeriodName && parsed.periodMaxPerUser) {
        return { salePeriodId: parsed.salePeriodId, salePeriodName: parsed.salePeriodName, periodMaxPerUser: parsed.periodMaxPerUser }
      }
    } catch {
      // fall through to derived id
    }
  }
  return {
    salePeriodId: periodId(source.title, source.startDate.slice(0, 10), source.endDate.slice(0, 10), source.maxPerUser),
    salePeriodName: source.title,
    periodMaxPerUser: source.maxPerUser,
  }
}

export function createSupabaseSupplyRepository(client: SupabaseClient): SupplyRepository {
  return {
    async listVisibleOfferings(context: TenantContext, { limit }: SupplyListInput) {
      const { data, error } = await client
        .from('supply_offerings')
        .select('id, organization_id, title, description, unit_price, currency, total_stock, remaining_stock, max_per_user, status, start_date, end_date, product:supply_products(id, name, unit)')
        .eq('organization_id', context.organizationId)
        .eq('status', 'active')
        .order('end_date', { ascending: true })
        .limit(Math.max(1, Math.min(limit, 200)))

      if (error) throw error

      return (data ?? []).map((item): SupplyOffering => {
        const product = item.product as unknown as { id: UUID; name: string; unit: string } | null
        return {
          id: item.id as UUID,
          organizationId: item.organization_id as UUID,
          productId: product?.id ?? '',
          title: item.title as string,
          description: item.description as string | null,
          unitPrice: Number(item.unit_price),
          currency: item.currency as string,
          totalStock: item.total_stock as number,
          remainingStock: item.remaining_stock as number,
          maxPerUser: item.max_per_user as number,
          status: item.status as SupplyOffering['status'],
          startDate: item.start_date as string,
          endDate: item.end_date as string,
          createdBy: '',
          createdAt: item.start_date as string,
          updatedAt: item.start_date as string,
          product: product
            ? {
                id: product.id,
                organizationId: item.organization_id as UUID,
                name: product.name,
                description: null,
                imageUrl: null,
                unit: product.unit,
                isActive: true,
                createdBy: '',
                createdAt: item.start_date as string,
                updatedAt: item.start_date as string,
                deletedAt: null,
              }
            : undefined,
        }
      })
    },

    async reserve(context: TenantContext, offeringId: UUID, quantity: number) {
      if (!context.membershipId) throw new Error('Membership is required to reserve an offering')
      if (!Number.isInteger(quantity) || quantity <= 0) throw new Error('Quantity must be a positive integer')

      const { data: offerings, error: offeringsError } = await client
        .from('supply_offerings')
        .select('id, title, description, status, start_date, end_date, remaining_stock, max_per_user')
        .eq('organization_id', context.organizationId)
        .limit(200)
      if (offeringsError) throw offeringsError

      const offering = offerings.find((item) => item.id === offeringId)
      if (!offering) throw new Error('Offering not found or no longer available')
      if (offering.status !== 'active') throw new Error('Offering is no longer active')

      const now = Date.now()
      if (new Date(offering.start_date).getTime() > now || new Date(offering.end_date).getTime() < now) {
        throw new Error('Offering is outside its sales period')
      }
      if (quantity > offering.remaining_stock) throw new Error('Not enough stock available')

      const selectedMeta = parseSalePeriodMeta({
        title: offering.title, description: offering.description, startDate: offering.start_date,
        endDate: offering.end_date, maxPerUser: offering.max_per_user,
      })
      const offeringsById = new Map(offerings.map((item) => [item.id as UUID, item]))

      const { data: myReservations, error: reservationsError } = await client
        .from('supply_reservations')
        .select('offering_id, quantity, status')
        .eq('membership_id', context.membershipId)
        .limit(200)
      if (reservationsError) throw reservationsError

      const reservedInPeriod = (myReservations ?? []).reduce((total, reservation) => {
        if (!activeReservationStatuses.has(reservation.status as string)) return total
        const reservedOffering = offeringsById.get(reservation.offering_id as UUID)
        if (!reservedOffering) return total
        const reservedMeta = parseSalePeriodMeta({
          title: reservedOffering.title, description: reservedOffering.description,
          startDate: reservedOffering.start_date, endDate: reservedOffering.end_date, maxPerUser: reservedOffering.max_per_user,
        })
        return reservedMeta.salePeriodId === selectedMeta.salePeriodId ? total + (reservation.quantity as number) : total
      }, 0)

      if (reservedInPeriod + quantity > selectedMeta.periodMaxPerUser) throw new Error('Period limit exceeded')

      const { data, error } = await client.functions.invoke('create-reservation-preference', {
        body: { organizationId: context.organizationId, offeringId, quantity },
      })
      if (error) throw error

      const initPoint = (data as { initPoint?: string } | null)?.initPoint
      if (!initPoint) throw new Error('No se pudo iniciar el pago de la compra.')
      return { initPoint }
    },

    async createOffering(context: TenantContext, input: CreateSupplyOfferingInput) {
      const { data: product, error: productError } = await client
        .rpc('create_supply_product', {
          p_organization_id: context.organizationId,
          p_name: input.productName,
          p_description: null,
          p_unit: input.productUnit ?? 'unidad',
        })
        .single()
      if (productError) throw productError

      const { error } = await client.rpc('create_supply_offering', {
        p_organization_id: context.organizationId,
        p_product_id: (product as { id: UUID }).id,
        p_title: input.title,
        p_unit_price: Math.round(input.unitPrice),
        p_total_stock: input.totalStock,
        p_start_date: input.startDate,
        p_end_date: input.endDate,
        p_description: input.description ?? null,
        p_currency: input.currency ?? 'CLP',
        p_max_per_user: input.maxPerUser ?? 1,
      })
      if (error) throw error
    },

    async deleteOffering(context: TenantContext, offeringId: UUID) {
      const { error } = await client.rpc('delete_supply_offering', { p_id: offeringId, p_organization_id: context.organizationId })
      if (error) throw error
    },

    async updateOffering(context: TenantContext, offeringId: UUID, input) {
      const { error } = await client.rpc('update_supply_offering', {
        p_id: offeringId,
        p_organization_id: context.organizationId,
        p_title: input.title,
        p_unit_price: Math.round(input.unitPrice),
        p_total_stock: input.totalStock,
        p_remaining_stock: input.remainingStock,
        p_start_date: input.startDate,
        p_end_date: input.endDate,
        p_description: input.description ?? null,
        p_max_per_user: input.maxPerUser ?? 1,
      })
      if (error) throw error
    },

    async listReservations(context: TenantContext, { limit }: SupplyListInput) {
      const { data, error } = await client
        .from('supply_reservations')
        .select(`
          id, quantity, status, notes, delivered_at, created_at,
          offering:supply_offerings!inner (id, title, organization_id),
          membership:memberships!membership_id (id, institutional_email, person:persons (preferred_name, rut))
        `)
        .eq('offering.organization_id', context.organizationId)
        .order('created_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 200)))

      if (error) throw error

      return (data ?? []).map((item): SupplyReservation => {
        const offering = item.offering as unknown as { id: UUID; title: string }
        const membership = item.membership as unknown as { id: UUID; institutional_email: string | null; person: { preferred_name: string; rut: string | null } | null }
        return {
          id: item.id as UUID,
          organizationId: context.organizationId,
          offeringId: offering.id,
          userId: membership.id,
          quantity: item.quantity as number,
          unitPrice: 0,
          totalAmount: 0,
          currency: 'CLP',
          status: item.status as SupplyReservation['status'],
          transactionId: null,
          deliveredAt: (item.delivered_at as string | null) ?? null,
          deliveredBy: null,
          notes: (item.notes as string | null) ?? null,
          createdAt: item.created_at as string,
          updatedAt: item.created_at as string,
          offering: {
            id: offering.id, organizationId: context.organizationId, productId: '', title: offering.title,
            description: null, unitPrice: 0, currency: 'CLP', totalStock: 0, remainingStock: 0, maxPerUser: 1,
            status: 'active', startDate: item.created_at as string, endDate: item.created_at as string,
            createdBy: '', createdAt: item.created_at as string, updatedAt: item.created_at as string,
          },
          user: {
            id: membership.id, firebaseUid: '', email: membership.institutional_email || '',
            displayName: membership.person?.preferred_name ?? '', avatarUrl: null, phone: null,
            rut: membership.person?.rut ?? null, emailVerified: false, isSuperAdmin: false, status: 'active',
            lastLoginAt: null, createdAt: item.created_at as string, updatedAt: item.created_at as string,
          },
        }
      })
    },

    async markDelivered(context: TenantContext, reservationId: UUID) {
      const { error } = await client.rpc('mark_reservation_delivered', { p_id: reservationId, p_organization_id: context.organizationId })
      if (error) throw error
    },

    async cancelReservation(context: TenantContext, reservationId: UUID) {
      const { error } = await client.rpc('cancel_reservation', { p_id: reservationId, p_organization_id: context.organizationId })
      if (error) throw error
    },

    async listMyReservations(context: TenantContext, limit: number) {
      if (!context.membershipId) return []

      const { data, error } = await client
        .from('supply_reservations')
        .select('id, quantity, status, created_at, offering:supply_offerings(id, title, unit_price, currency)')
        .eq('membership_id', context.membershipId)
        .order('created_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 200)))

      if (error) throw error

      return (data ?? []).map((item): SupplyReservation => {
        const offering = item.offering as unknown as { id: UUID; title: string; unit_price: number; currency: string }
        return {
          id: item.id as UUID,
          organizationId: context.organizationId,
          offeringId: offering.id,
          userId: context.membershipId as UUID,
          quantity: item.quantity as number,
          unitPrice: Number(offering.unit_price),
          totalAmount: Number(offering.unit_price) * (item.quantity as number),
          currency: offering.currency || 'CLP',
          status: item.status as SupplyReservation['status'],
          transactionId: null,
          deliveredAt: null,
          deliveredBy: null,
          notes: null,
          createdAt: item.created_at as string,
          updatedAt: item.created_at as string,
          offering: {
            id: offering.id, organizationId: context.organizationId, productId: '', title: offering.title,
            description: null, unitPrice: Number(offering.unit_price), currency: offering.currency || 'CLP',
            totalStock: 0, remainingStock: 0, maxPerUser: 1, status: 'active',
            startDate: item.created_at as string, endDate: item.created_at as string,
            createdBy: '', createdAt: item.created_at as string, updatedAt: item.created_at as string,
          },
          user: {
            id: context.membershipId as UUID, firebaseUid: '', email: '', displayName: '', avatarUrl: null,
            phone: null, rut: null, emailVerified: false, isSuperAdmin: false, status: 'active',
            lastLoginAt: null, createdAt: item.created_at as string, updatedAt: item.created_at as string,
          },
        }
      })
    },

    async getSupplyConfig(context: TenantContext) {
      const { data, error } = await client
        .from('supply_configs')
        .select('organization_id, is_limit_active, max_monthly_qty, unit_label, description')
        .eq('organization_id', context.organizationId)
        .maybeSingle()

      if (error) throw error
      if (!data) return null

      return {
        organizationId: data.organization_id as UUID,
        isLimitActive: data.is_limit_active as boolean,
        maxMonthlyQty: data.max_monthly_qty as number,
        unitLabel: data.unit_label as string,
        description: data.description as string,
      }
    },

    async saveSupplyConfig(context: TenantContext, input) {
      const { error } = await client
        .from('supply_configs')
        .upsert({
          organization_id: context.organizationId,
          is_limit_active: input.isLimitActive,
          max_monthly_qty: input.maxMonthlyQty,
          unit_label: input.unitLabel,
          description: input.description,
        })
      if (error) throw error
    },

    async listAgreementInfos(context: TenantContext, limit: number) {
      const { data, error } = await client
        .from('agreement_infos')
        .select('id, organization_id, title, description, discount_details, partner_name, contact_phone, website_url, created_at, updated_at')
        .eq('organization_id', context.organizationId)
        .order('created_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 200)))

      if (error) throw error

      return (data ?? []).map((item): AgreementInfo => ({
        id: item.id as UUID,
        organizationId: item.organization_id as UUID,
        title: item.title as string,
        description: item.description as string,
        discountDetails: (item.discount_details as string | null) ?? null,
        partnerName: item.partner_name as string,
        contactPhone: (item.contact_phone as string | null) ?? null,
        websiteUrl: (item.website_url as string | null) ?? null,
        createdAt: item.created_at as string,
        updatedAt: item.updated_at as string,
      }))
    },

    async createAgreementInfo(context: TenantContext, input) {
      const { error } = await client.from('agreement_infos').insert({
        organization_id: context.organizationId,
        title: input.title,
        description: input.description,
        discount_details: input.discountDetails ?? null,
        partner_name: input.partnerName,
        contact_phone: input.contactPhone ?? null,
        website_url: input.websiteUrl ?? null,
      })
      if (error) throw error
    },

    async updateAgreementInfo(context: TenantContext, id: UUID, input) {
      const { error } = await client
        .from('agreement_infos')
        .update({
          title: input.title,
          description: input.description,
          discount_details: input.discountDetails ?? null,
          partner_name: input.partnerName,
          contact_phone: input.contactPhone ?? null,
          website_url: input.websiteUrl ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('organization_id', context.organizationId)
      if (error) throw error
    },

    async deleteAgreementInfo(context: TenantContext, id: UUID) {
      const { error } = await client.from('agreement_infos').delete().eq('id', id).eq('organization_id', context.organizationId)
      if (error) throw error
    },
  }
}
